import { lazy } from 'react';

// project imports
import Loadable from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';
import AuthGuard from 'utils/route-guard/AuthGuard';
import PermissionGuard from 'utils/route-guard/PermissionGuard';

// pages routing
const MaintenanceError = Loadable(lazy(() => import('pages/maintenance/404')));
const MaintenanceError500 = Loadable(lazy(() => import('pages/maintenance/500')));
const RouteErrorBoundary = Loadable(lazy(() => import('pages/maintenance/RouteErrorBoundary')));
const NoPermission = Loadable(lazy(() => import('pages/apps/permission/no-permission')));

const Dashboard = Loadable(lazy(() => import('pages/dashboard/dashboard')));
const TenantDashboard = Loadable(lazy(() => import('pages/dashboard/tenant-dashboard')));
const ContractorDashboard = Loadable(lazy(() => import('pages/dashboard/contractor-dashboard')));
const States = Loadable(lazy(() => import('pages/apps/states/list')));
const CountyPage = Loadable(lazy(() => import('pages/apps/county/list')));
const CityListPage = Loadable(lazy(() => import('pages/apps/city/list')));
const Roles = Loadable(lazy(() => import('pages/apps/role/list')));
const AddressListPage = Loadable(lazy(() => import('pages/apps/MasterAddressList/list')));

const MyProfile = Loadable(lazy(() => import('pages/profile/user')));
const MySignatures = Loadable(lazy(() => import('pages/profile/MySignatures')));
const Users = Loadable(lazy(() => import('pages/apps/users/list')));
const RolePermissions = Loadable(lazy(() => import('pages/apps/role/permissions/list')));
const LoginLogs = Loadable(lazy(() => import('pages/apps/loginLogs/list')));
const EmailLogs = Loadable(lazy(() => import('pages/apps/emaillogs/list')));
const Tenants = Loadable(lazy(() => import('pages/apps/tenants/list')));
const TenantForm = Loadable(lazy(() => import('sections/apps/tenant/TenantForm')));
const MunicipalityForm = Loadable(lazy(() => import('sections/apps/city/MunicipalityForm')));
const Projects = Loadable(lazy(() => import('pages/apps/contractor/project/list')));
const ProjectWorkSpace = Loadable(lazy(() => import('pages/apps/contractor/project/projectWorkSpace/ProjectWorkSpacelist')));
const PPFUserProjects = Loadable(lazy(() => import('pages/apps/ppfuser/project/list')));
const PPFContractorManagement = Loadable(lazy(() => import('pages/contractors/list')));
/// Contractor Management PPF
const EditContractorForm = Loadable(lazy(() => import('sections/contractors/EditContractorForm')));
/// END Contractor Management PPF

const PPFProjectWorkSpace = Loadable(lazy(() => import('pages/apps/ppfuser/project/ppfProjectWorkSpace/PPFProjectWorkSpacelist')));
const PermitAndInspection = Loadable(lazy(() => import('pages/permitsandinspection/list')));
const Services = Loadable(lazy(() => import('pages/services/list')));
const Municipalities = Loadable(lazy(() => import('pages/municipality/list')));

const CompanyProfile = Loadable(lazy(() => import('sections/apps/ppf/companyprofile/CompanyProfile')));
const ProposalForm = Loadable(lazy(() => import('pages/apps/contractor/project/projectWorkSpace/proposal/list')));
const ProposalTemplateForm = Loadable(lazy(() => import('sections/apps/proposaltemplate/ProposalTemplateForm')));
const ProposalTemplate = Loadable(lazy(() => import('pages/apps/proposaltemplate/list')));
const MasterAgreementTemplate = Loadable(lazy(() => import('pages/masterAgreementTemplate/list')));
const MasterAgreementTemplateEditor = Loadable(lazy(() => import('sections/masterAgreementTemplate/MasterAgreementTemplateEditor')));
//const DefaultFileCategory = Loadable(lazy(() => import('pages/defaultFileCategories/list')));
const DefaultDirectory = Loadable(lazy(() => import('pages/defaultdirectories/list')));
const MasterAgreementViewer = Loadable(lazy(() => import('sections/contractors/MasterAgreementViewer')));
const MasterAgreementForm = Loadable(lazy(() => import('sections/contractors/MasterAgreementForm')));
const ContractorMasterAgreementViewer = Loadable(lazy(() => import('sections/apps/contractor/masteragreement/ContractorMasterAgreementViewer')));
const PPFInvoices = Loadable(lazy(() => import('pages/apps/ppfuser/invoice/list')));
const InvoiceForm = Loadable(lazy(() => import('sections/apps/ppf/invoice/InvoiceForm')));
const Payments = Loadable(lazy(() => import('pages/payments/list')));
const ContractorInvoiceList = Loadable(lazy(() => import('pages/apps/contractor/Invoice/list')));
const PaymentsForm = Loadable(lazy(() => import('sections/payments/PaymentForm')));

const Credits = Loadable(lazy(() => import('pages/apps/ppfuser/transactions/credits/list')));
const CreditEditor = Loadable(lazy(() => import('sections/apps/ppf/transactions/credit/CreditForm')));

const ContractorSubscription = Loadable(lazy(() => import('pages/apps/ppfuser/transactions/contractorSubscription/list')));
const ContractorSubscriptionEditor = Loadable(lazy(() => import('sections/apps/ppf/transactions/contractorSubscription/ContractorSubscriptionForm')));
const Subscriptions = Loadable(lazy(() => import('pages/apps/contractor/subscriptions/list')));

const ContractorCredits = Loadable(lazy(() => import('pages/apps/contractor/transactions/credits/list')))

const ParentCompanyList = Loadable(lazy(() => import('pages/apps/ppfuser/parentCompany/list')));
const PermitTypeSelection = Loadable(lazy(() => import('sections/apps/users/permitTypeSelection/PermitTypeSelection')));
const ContractorCompanyProfile = Loadable(lazy(() => import('pages/apps/contractor/companyProfile/CompanyProfile')));
const PlanReviewsListPage = Loadable(lazy(() => import('pages/planReviews/list')));
const PlanReviewEditor = Loadable(lazy(() => import('sections/planReviews/editor/PlanReviewEditor')));
const TaskTemplatesListPage = Loadable(lazy(() => import('pages/taskTemplates/list')));
const TaskTemplateEditor = Loadable(lazy(() => import('sections/taskTemplates/editor/TaskTemplateEditor')));
const TaskListPage = Loadable(lazy(() => import('pages/taskList/list')));
const TaskBoard = Loadable(lazy(() => import('pages/TaskBoard/list')));
const InspectionListPage = Loadable(lazy(() => import('pages/inspections/list')));
const InspectionEditorPage = Loadable(lazy(() => import('sections/inspections/InspectionEditor/InspectionEditor')));
const NotifyInspectionEmailTemplate = Loadable(lazy(() => import('pages/apps/inspectionEmailTemplates/notify')));
const ResultInspectionEmailTemplate = Loadable(lazy(() => import('pages/apps/inspectionEmailTemplates/result')));
const NotaryListPage = Loadable(lazy(() => import('pages/notary/list')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  errorElement: <RouteErrorBoundary />,
  children: [
    {
      path: '/',
      element: (
        <AuthGuard>
          <PermissionGuard>
            <DashboardLayout />
          </PermissionGuard>
        </AuthGuard>
      ),
      children: [
        {
          path: 'dashboard',
          element: <Dashboard />
        },
        {
          path: 'tenant-dashboard',
          element: <TenantDashboard />
        },
        {
          path: 'contractor-dashboard',
          element: <ContractorDashboard />
        },
        /// Contractor Management PPF
        {
          path: 'contractors',
          element: <PPFContractorManagement />
        },

        {
          path: 'contractors/view/:contractorID',
          element: <EditContractorForm isView={true} />
        },
        {
          path: 'contractors/edit/:contractorID',
          element: <EditContractorForm />
        },
        /// END Contractor Management PPF

        {
          path: 'contractors/masteragreement/view/:masterAgreementID',
          element: <MasterAgreementViewer />
        },
        { ///Tenant/Contraor/MasterAgreement/{MasterAgreementID}
          path: 'contractors/masteragreement/:masterAgreementID',
          element: <MasterAgreementForm />
        },
        {
          path: 'settings/masterAgreementTemplates',
          element: <MasterAgreementTemplate />
        },
        {
          path: 'settings/notaries',
          element: <NotaryListPage />
        },
        {
          path: 'settings/masterAgreementTemplates/edit/:masterAgreementTemplateID',
          element: <MasterAgreementTemplateEditor />
        },
        // {
        //   path: 'settings/defaultFileCategories/project',
        //   element: <DefaultFileCategory />
        // },
        // {
        //   path: 'settings/defaultFileCategories/municipality',
        //   element: <DefaultFileCategory />
        // },
        {
          path: 'settings/taskTemplates',
          element: <TaskTemplatesListPage />
        },
        {
          path: 'settings/taskTemplates/:mode/:taskTemplateID',
          element: <TaskTemplateEditor />
        },
        {
          path: 'ppf/taskList',
          element: <TaskListPage />
        },
        {
          path: 'settings/defaultfolder/project',
          element: <DefaultDirectory />
        },
        {
          path: 'settings/defaultfolder/municipality',
          element: <DefaultDirectory />
        },
        {
          path: 'settings/service',
          element: <Services />
        },
        {
          path: 'settings/inspection-email-templates/notify',
          element: <NotifyInspectionEmailTemplate />
        },
        {
          path: 'settings/inspection-email-templates/result',
          element: <ResultInspectionEmailTemplate />
        },
        {
          path: 'ppf/inspections',
          element: <InspectionListPage />
        },
        {
          path: 'ppf/inspections/editor/:inspectionId',
          element: <InspectionEditorPage />
        },
        {
          path: 'ppf/inspections/editor',
          element: <InspectionEditorPage />
        },


        /// PPF User Portal
        {
          path: 'ppf/invoices',
          element: <PPFInvoices />
        },
        {
          path: 'ppf/invoices/new',
          element: <InvoiceForm />
        },
        {
          path: 'ppf/invoices/edit/:invoiceID',
          element: <InvoiceForm />
        },

        {
          path: 'ppf/invoices/view/:invoiceID',
          element: <InvoiceForm />
        },
        {
          path: 'ppf/payments',
          element: <Payments />
        },
        {
          path: 'ppf/payments/new',
          element: <PaymentsForm />
        },
        {
          path: 'ppf/payments/view/:paymentID',
          element: <PaymentsForm />
        },
        {
          path: 'ppf/payments/edit/:paymentID',
          element: <PaymentsForm />
        },
        {
          path: 'ppf/planReviews',
          element: <PlanReviewsListPage />
        },
        {
          path: 'ppf/planReviews/:projectID',
          element: <PlanReviewEditor />
        },
        {
          path: 'ppf/taskboard',
          element: <TaskBoard />
        },
        {
          path: 'contractors/subscriptions',
          element: <Subscriptions />
        },
        {
          path: 'contractors/companyprofile',
          element: <ContractorCompanyProfile />
        },
        {
          path: 'apps',
          children: [
            {
              path: 'tenants',
              element: <Tenants />
            },
            {
              path: 'tenants/new',
              element: <TenantForm />
            },
            {
              path: 'tenants/edit/:tenantID',
              element: <TenantForm />
            },
            {
              path: 'tenants/view/:tenantID',
              element: <TenantForm />
            },
            /// Contractor Portal
            {
              path: 'contractors/projects',
              element: <Projects />
            },
            {
              path: 'contractors/masteragreement/:masterAgreementID?',
              element: <ContractorMasterAgreementViewer />
            },
            {
              path: 'contractors/masteragreement',
              element: <ContractorMasterAgreementViewer />
            },

            {
              path: 'contractors/invoices',
              element: <ContractorInvoiceList />
            },

            /// End Contractor POrtal Side

            {
              path: 'contractors/projects/workspace/:projectID',
              element: <ProjectWorkSpace />
            },
            {
              path: 'contractor/transactions/credits',
              element: <ContractorCredits />
            },
            {
              path: 'ppf/projects',
              element: <PPFUserProjects />
            },
            {
              path: 'ppf/projects/workspace/:projectID',
              element: <PPFProjectWorkSpace />
            },
            {
              path: 'ppf/project/proposal/:proposalID',
              element: <ProposalForm />
            },
            {
              path: 'ppf/companyprofile',
              element: <CompanyProfile />
            },
            {
              path: 'ppf/parentCompanies',
              element: <ParentCompanyList />
            },
            {
              path: 'ppf/transactions/credits',
              element: <Credits />
            },
            {
              path: 'ppf/transactions/credits/new',
              element: <CreditEditor />
            },
            {
              path: 'ppf/transactions/credits/edit/:id',
              element: <CreditEditor />
            },
            {
              path: 'ppf/transactions/credits/view/:id',
              element: <CreditEditor />
            },
            {
              path: 'ppf/transactions/contractorsubscriptions',
              element: <ContractorSubscription />
            },
            {
              path: 'ppf/transactions/contractorsubscriptions/new',
              element: <ContractorSubscriptionEditor />
            },
            {
              path: 'ppf/transactions/contractorsubscriptions/edit/:id',
              element: <ContractorSubscriptionEditor />
            },
            {
              path: 'ppf/transactions/contractorsubscriptions/view/:id',
              element: <ContractorSubscriptionEditor />
            },
            {
              path: 'states',
              element: <States />
            },
            {
              path: 'counties',
              element: <CountyPage />
            },
            {
              path: 'cities',
              element: <CityListPage />
            },
            {
              path: 'masteraddress',
              element: <AddressListPage />
            },
            {
              path: 'municipalities',
              element: <Municipalities />
            },
            {
              path: 'municipalities/:municipalityID',
              element: <MunicipalityForm />
            },
            {
              path: 'cities/municipality/:municipalityID',
              element: <MunicipalityForm />
            },
            {
              path: 'counties/municipality/:municipalityID',
              element: <MunicipalityForm />
            },
            {
              path: 'profiles',
              children: [
                {
                  path: 'user',
                  element: <MyProfile />
                },
                {
                  path: 'signatures',
                  element: <MySignatures />
                }
              ]
            },
            {
              path: 'users',
              element: <Users />
            },
            {
              path: 'users/permittypeConfiguration',
              element: <PermitTypeSelection />
            },
            {
              path: 'roles',
              element: <Roles />
            },
            {
              path: 'roles/permissions/:userRoleID',
              element: <RolePermissions />
            },
            {
              path: 'emailLogs',
              element: <EmailLogs />
            },
            {
              path: 'loginLogs',
              element: <LoginLogs />
            },
            {
              path: 'proposaltemplates',
              element: <ProposalTemplate />
            },
            {
              path: 'proposaltemplates/new',
              element: <ProposalTemplateForm />
            },
            {
              path: 'proposaltemplates/edit/:proposalTemplateID',
              element: <ProposalTemplateForm />
            },
            {
              path: 'proposaltemplates/view/:proposalTemplateID',
              element: <ProposalTemplateForm />
            },
          ]
        },


        {
          path: 'permit&Inspections',
          element: <PermitAndInspection />
        },
        {
          path: 'no-permission',
          element: <NoPermission />
        },
        {
          path: 'error/500',
          element: <MaintenanceError500 />
        },
        {
          path: '*',
          element: <MaintenanceError />
        }
      ]
    }
  ]
};

export default MainRoutes;
